/**
 * Payroll export — non-envelope endpoint.
 *
 * The api streams CSV directly with a `Content-Disposition: attachment`
 * header, so we don't go through `apiRequest` (which assumes JSON).
 * Trigger a browser download by creating an object URL from the Blob
 * and clicking a synthetic anchor.
 */

const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/+$/, '')

export interface PayrollExportQuery {
  from: string // ISO date — YYYY-MM-DD
  to: string // ISO date — YYYY-MM-DD
}

export interface PayrollExportResult {
  filename: string
  rows: number
}

/** Hit `/api/v1/payroll/export.csv` and trigger a browser download.
 *  Returns `{filename, rows}` so the UI can show "exported N rows". */
export async function downloadPayrollCsv(
  query: PayrollExportQuery,
): Promise<PayrollExportResult> {
  const params = new URLSearchParams({ from: query.from, to: query.to })
  const url = `${API_BASE}/api/v1/payroll/export.csv?${params.toString()}`

  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'text/csv' },
  })

  if (!response.ok) {
    let detail = ''
    try {
      detail = (await response.json()).error ?? ''
    } catch {
      detail = await response.text()
    }
    throw new Error(detail || `Export failed (${response.status})`)
  }

  const text = await response.text()
  // Header row + N data rows. Last newline is optional, so trim once.
  const newlineCount = text.replace(/\n$/, '').split('\n').length
  const dataRows = Math.max(0, newlineCount - 1)

  // Pull filename out of Content-Disposition; fall back to a sensible
  // default if the header is missing.
  const disposition = response.headers.get('content-disposition') ?? ''
  const match = disposition.match(/filename="([^"]+)"/)
  const filename = match?.[1] ?? `lms-payroll-${query.from}_to_${query.to}.csv`

  // Trigger the browser download via a synthetic anchor — same pattern
  // every browser respects without needing a `download` server header
  // beyond Content-Disposition.
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' })
  const objectUrl = URL.createObjectURL(blob)
  try {
    const anchor = document.createElement('a')
    anchor.href = objectUrl
    anchor.download = filename
    document.body.appendChild(anchor)
    anchor.click()
    document.body.removeChild(anchor)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }

  return { filename, rows: dataRows }
}
